import { useCallback, useEffect, useState } from "react";

import {
	Button,
	CircularProgress,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	MenuProps,
	Select,
	TextField,
	Typography,
} from "@material-ui/core";
import { Theme, createStyles, makeStyles } from "@material-ui/core/styles";

import { ColumnApi, GridApi, GridReadyEvent } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-enterprise";
import "ag-grid-enterprise/styles/ag-theme-balham.min.css";

import { AgGridReact } from "ag-grid-react";

import { RootType } from "@/models/lookup-tables/lookup-tables.dto";
import { useComponentTypesContext } from "@/pages/configurations/componentTypes/context/ComponentTypeContext";
import { getPartByComponentType } from "@/pages/configurations/partComponentTypes/repositories/PartComponentTypeRepository";
import { useRootTypeContext } from "@/pages/configurations/rootTypes/context/RootTypeContext";
import { find } from "lodash-es";
import { ComponentTypesList } from "../ComponentTypesList/ComponentTypesList";
import { CreatePart } from "../CreatePart/CreatePart";
import { DeletePart } from "../DeletePart/DeletePart";
import { EditPart } from "../EditPart/EditPart";
import { useTableData } from "./customHooks/useTableData";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			"& .MuiDataGrid-row": {
				cursor: "pointer",
			},
			"& .MuiDataGrid-columnHeaderTitle, & .MuiTablePagination-caption": {
				fontWeight: "bold",
				fontSize: "12px",
			},
			"& .MuiDataGrid-columnHeaderTitleContainer": {
				padding: "0",
			},
			"& .MuiDataGrid-cell--textLeft": {
				fontSize: "12px",
			},
		},
		relative: {
			position: "relative",
		},
		textfield: {
			margin: theme.spacing(2),
		},
		btnModal: {
			display: "flex",
			justifyContent: "flex-end",
		},
		dataTable: {
			padding: theme.spacing(1, 2),
		},
	}),
);

type Props = {
	isLoading: boolean;
	getPartsFromAPI: Function;
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuPropss: Partial<MenuProps> = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
	getContentAnchorEl: null,
	anchorOrigin: {
		vertical: "bottom",
		horizontal: "center",
	},
	transformOrigin: {
		vertical: "top",
		horizontal: "center",
	},
	variant: "menu",
};

export const PartsTableData = ({ isLoading, getPartsFromAPI }: Props) => {
	const [gridAPI, setGridAPI] = useState<GridApi | null>(null);
	const [gridColumnAPI, setGridColumnAPI] = useState<ColumnApi | null>(null);

	const [filterInput, setFilterInput] = useState("");
	const [componentTypeFilter, setComponentTypeFilter] = useState(0);
	const [rootTypeFilter, setRootTypeFilter] = useState("");

	const [modalCreatePart, setModalCreatePart] = useState(false);
	const [modalEditPart, setModalEditPart] = useState(false);
	const [modalDeletePart, setModalDeletePart] = useState(false);
	const [modalComponentType, setModalComponentType] = useState(false);

	const [partIdForEdit, setPartIdForEdit] = useState<number | null>(null);
	const [partIdForDelete, setPartIdForDelete] = useState<number | null>(null);

	const classes = useStyles();

	const {
		state: { rootTypes },
	} = useRootTypeContext();
	const {
		state: { componentTypes },
	} = useComponentTypesContext();

	const { rows, columns, defaultColDef } = useTableData({
		setPartIdForEdit,
		setModalEditPart,
		setPartIdForDelete,
		setModalDeletePart,
		setModalComponentType,
	});

	const onGridReady = (params: GridReadyEvent) => {
		setGridAPI(params.api);
		setGridColumnAPI(params.columnApi);
	};

	const handlerFilter = (e: any) => {
		setFilterInput(e.target.value);
	};

	const handleRootTypeChange = (e: any) => {
		setRootTypeFilter(e.target.value as string);
	};

	const handleComponentTypeChange = async (e: any) => {
		setComponentTypeFilter(e.target.value);
	};

	useEffect(() => {
		if (componentTypeFilter) {
			(async () => {
				gridAPI?.showLoadingOverlay();
				const { data } = await getPartByComponentType(componentTypeFilter);

				const partsIds = Object.keys(data?.data.result);

				const partIdFilterInstance = gridAPI?.getFilterInstance("partId");

				partIdFilterInstance?.setModel({
					filterType: "set",
					values: partsIds,
				});

				gridAPI?.onFilterChanged();

				gridAPI?.hideOverlay();
			})();
		} else {
			const partIdFilterInstance = gridAPI?.getFilterInstance("partId");
			partIdFilterInstance?.setModel(null);
			gridAPI?.onFilterChanged();
		}
	}, [componentTypeFilter]);

	const onFilterTextBoxChanged = useCallback(() => {
		gridAPI?.setQuickFilter(filterInput);
	}, [filterInput]);

	useEffect(() => {
		if (isLoading) {
			gridAPI?.showLoadingOverlay();
		} else {
			gridAPI?.hideOverlay();
		}
	}, [isLoading, gridAPI]);

	useEffect(() => {
		gridAPI?.setQuickFilter(filterInput);
	}, [filterInput, gridAPI]);

	return (
		<Grid container justifyContent="center">
			<Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
				<Typography
					variant="h5"
					style={{ margin: "20px 0 20px", fontWeight: 600 }}
				>
					Parts Management
				</Typography>
			</Grid>
			<Grid
				container
				justifyContent="center"
				alignItems="center"
				className={classes.root}
			>
				<Grid
					item
					md={12}
					xs={12}
					style={{ padding: "0 15px" }}
					className={classes.relative}
				>
					<CreatePart
						show={modalCreatePart}
						onHide={(shouldUpdate: boolean) => {
							setModalCreatePart(false);
							if (shouldUpdate) getPartsFromAPI().then(() => {});
						}}
					/>
					<DeletePart
						partIdForDelete={partIdForDelete}
						show={modalDeletePart}
						onHide={(shouldUpdate: boolean) => {
							setModalDeletePart(false);
							if (shouldUpdate) getPartsFromAPI().then(() => {});
						}}
					/>
					<EditPart
						partIdForEdit={partIdForEdit}
						show={modalEditPart}
						onHide={(shouldUpdate: boolean) => {
							setModalEditPart(false);
							if (shouldUpdate) getPartsFromAPI().then(() => {});
						}}
					/>{" "}
					<ComponentTypesList
						partId={partIdForEdit}
						show={modalComponentType}
						onHide={(shouldUpdate: boolean) => {
							setModalComponentType(false);
							if (shouldUpdate) getPartsFromAPI().then(() => {});
						}}
					/>

					<div
						className="ag-theme-balham"
						style={{ width: "100%", height: "60vh" }}
					>
						<Grid
							container
							spacing={2}
							md={12}
							xs={12}
							style={{ width: "100rem", padding: "10px 0px" }}
						>
							<Grid item xs={12} md={4}>
								<FormControl style={{ width: "100%" }} variant="outlined">
									<InputLabel id="root-type-select-label">Root Type</InputLabel>
									<Select
										style={{ width: "100%" }}
										value={rootTypeFilter}
										label="Root Type"
										onChange={handleRootTypeChange}
										disabled={isLoading}
									>
										<MenuItem value={""}>All</MenuItem>
										{!isLoading &&
											rootTypes?.map((rt) => {
												return (
													<MenuItem
														value={rt?.rootTypeName}
														key={rt?.rootTypeId}
													>
														{rt?.rootTypeName}
													</MenuItem>
												);
											})}
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} md={4}>
								<FormControl style={{ width: "100%" }} variant="outlined">
									<InputLabel id="component-type-select-label">
										Component Type
									</InputLabel>
									<Select
										MenuProps={MenuPropss}
										style={{ width: "100%" }}
										disabled={isLoading}
										value={componentTypeFilter}
										label="Component Type"
										onChange={handleComponentTypeChange}
									>
										<MenuItem value={""}>
											<strong>Show all</strong>
										</MenuItem>
										{!isLoading &&
											componentTypes
												.filter((ctType) => {
													const rt: RootType | undefined = find(rootTypes, [
														"rootTypeName",
														rootTypeFilter,
													]);

													if (!rootTypeFilter) return !ctType.isRepair;
													else {
														return (
															!ctType.isRepair &&
															ctType.rootTypeId == rt?.rootTypeId
														);
													}
												})
												.map((ct) => {
													return (
														<MenuItem
															value={ct?.componentTypeId}
															key={ct?.componentTypeId}
														>
															{ct?.componentTypeName}
														</MenuItem>
													);
												})}
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} md={4}>
								<TextField
									id="outlined-basic"
									label="Filter..."
									variant="outlined"
									style={{ width: "100%" }}
									value={filterInput}
									onChange={handlerFilter}
								/>
							</Grid>
						</Grid>
						<AgGridReact
							loadingOverlayComponent={CircularProgress}
							onGridReady={onGridReady}
							rowData={rows}
							columnDefs={columns}
							defaultColDef={defaultColDef}
						/>
					</div>
					<Grid container justifyContent="flex-end">
						<Grid item md={2} xs={12} className={classes.btnModal}>
							<Button
								variant="contained"
								color="primary"
								onClick={() => setModalCreatePart(!modalCreatePart)}
								fullWidth
								style={{ margin: "100px 0px 0px" }}
							>
								New part
							</Button>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};
